import { addScanFromScanner } from "actions";
import { reportGallagherTwxDataLoggerConfigFailed } from "actions/deviceDrivers/gallagherTwxDataLogger";
import {
  DeviceLike,
  getDeviceId,
} from "lib/deviceDrivers/sessionFramework/runtime/DriverSession";

export const GALLAGHER_TWX_DATA_LOGGER_DRIVER_NAME = "gallagher-twx-datalogger";

// Fields the device may send, as labelled on its config screen.
export enum DataValue {
  EID = "EID",
  VISUAL_TAG = "VID",
  WEIGHT = "WEIGHT",
  WEIGHT_UNITS = "WEIGHT_UNITS",
  CONDITION_SCORE = "CONDITION_SCORE",
  DRAFT_GATE = "DRAFT_GATE",
  ANIMAL_NOTES = "ANIMAL_NOTES",
}

// Fixed wire width per field, used to detect fields by segment length.
const FIELD_WIDTH: Record<DataValue, number> = {
  [DataValue.EID]: 16,
  [DataValue.VISUAL_TAG]: 16,
  [DataValue.WEIGHT]: 6,
  [DataValue.WEIGHT_UNITS]: 2,
  [DataValue.CONDITION_SCORE]: 3,
  [DataValue.DRAFT_GATE]: 1,
  [DataValue.ANIMAL_NOTES]: 30,
};

// Order the fields appear on the wire in, when present.
const FIELD_ORDER: DataValue[] = [
  DataValue.EID,
  DataValue.VISUAL_TAG,
  DataValue.WEIGHT,
  DataValue.WEIGHT_UNITS,
  DataValue.CONDITION_SCORE,
  DataValue.DRAFT_GATE,
  DataValue.ANIMAL_NOTES,
];

// EID and VID share a wire width, so a lone occurrence can't be told apart by length.
const UNAMBIGUOUS_FIELD_ORDER = FIELD_ORDER.filter(
  field => field !== DataValue.EID && field !== DataValue.VISUAL_TAG,
);

// A present EID is 15 digits with no separator, e.g. "942000000374966".
const EID_NO_SPACE_PATTERN = /^\d{15}$/;

const GRAMS_PER_POUND = 453.592;

type FieldValues = Partial<Record<DataValue, string>>;
type FieldToggles = Partial<Record<DataValue, boolean>>;

// Infers which comma-separated segments map to which fields. Values are extracted
// separately by parseFrameWithConfig, once the layout is known.
export function autoConfigure(frame: string): FieldToggles {
  const segments = frame.split(",");
  const fieldToggles: FieldToggles = {};

  segments.forEach(segment => {
    const field = UNAMBIGUOUS_FIELD_ORDER.find(
      candidate => FIELD_WIDTH[candidate] === segment.length,
    );
    if (field) {
      fieldToggles[field] = true;
    }
  });

  const sixteenCharSegments = segments.filter(
    segment => segment.length === FIELD_WIDTH[DataValue.EID],
  );

  if (sixteenCharSegments.length === 2) {
    // EID always precedes VID on the wire when both are present.
    fieldToggles[DataValue.EID] = true;
    fieldToggles[DataValue.VISUAL_TAG] = true;
  } else if (sixteenCharSegments.length === 1) {
    const trimmed = sixteenCharSegments[0].trim();

    // A blank segment defaults to EID; otherwise confirm it looks like one.
    const looksLikeEid = trimmed === "" || EID_NO_SPACE_PATTERN.test(trimmed);
    fieldToggles[looksLikeEid ? DataValue.EID : DataValue.VISUAL_TAG] = true;
  }

  return fieldToggles;
}

// Wire order is fixed, so the enabled toggles fully determine field order.
function getEnabledFields(fieldToggles: FieldToggles): DataValue[] {
  return FIELD_ORDER.filter(field => fieldToggles[field]);
}

// Extracts values for a known field layout. Fails closed (returns null) on any
// structural mismatch rather than guessing.
export function parseFrameWithConfig(
  frame: string,
  enabledFields: DataValue[],
): FieldValues | null {
  const segments = frame.split(",");

  if (segments.length !== enabledFields.length) {
    return null;
  }

  const fields: FieldValues = {};

  for (let index = 0; index < enabledFields.length; index += 1) {
    const field = enabledFields[index];
    const segment = segments[index];

    if (segment.length !== FIELD_WIDTH[field]) {
      return null;
    }

    fields[field] = segment.trim();
  }

  return fields;
}

// The last field layout that successfully parsed a frame. Kept in memory only (not
// persisted) - there's only ever one data logger connected at a time, and a fresh
// tab/reload just re-autoconfigures from the next frame.
let lastFieldToggles: FieldToggles | undefined;

// Reformats the raw 15-digit EID to the "CCC NNNNNNNNNNNN" display convention.
function formatRawEid(rawEid: string): string {
  return rawEid ? `${rawEid.slice(0, 3)} ${rawEid.slice(3)}` : "";
}

enum Unit {
  POUNDS = "lb",
  KILOGRAMS = "kg",
}

function weightToGrams(
  weightString: string,
  unitString: string,
): number | null {
  const weight = parseFloat(weightString);
  if (Number.isNaN(weight)) {
    return null;
  }
  return unitString.trim().toLowerCase() === Unit.POUNDS
    ? weight * GRAMS_PER_POUND
    : weight * 1000;
}

// Assumes fields[DataValue.EID] is already confirmed non-blank.
function buildScanActions(deviceId: string, fields: FieldValues) {
  const eid = formatRawEid(fields[DataValue.EID] ?? "");

  const now = new Date().toISOString();
  const totalMassGrams =
    DataValue.WEIGHT in fields
      ? weightToGrams(
          fields[DataValue.WEIGHT] ?? "",
          fields[DataValue.WEIGHT_UNITS] ?? "",
        )
      : null;

  const scan = {
    created: now,
    EID: eid,
    timeWeighed: totalMassGrams != null ? now : null,
    totalMassGrams,
    draftName: "",
  };

  return [addScanFromScanner(deviceId, scan)];
}

// Parses against a field layout, requiring a non-blank EID - a structural match with
// EID enabled but blank on the wire usually means VID mislabelled as EID (the two
// share a wire width), so it's treated the same as no match at all.
function parseWithEid(
  frame: string,
  fieldToggles: FieldToggles,
): FieldValues | null {
  const fields = parseFrameWithConfig(frame, getEnabledFields(fieldToggles));
  return fields?.[DataValue.EID] ? fields : null;
}

// The device terminates every complete frame with \r\n - its absence means this is a
// partial read, not a differently-shaped frame, so there's nothing to parse yet.
// trimEnd() isn't used here since it would also eat the space padding on a fixed-width
// final field, throwing off length-based field detection.
const FRAME_TERMINATOR = "\r\n";

function stripFrameTerminator(frame: string): string | null {
  return frame.endsWith(FRAME_TERMINATOR)
    ? frame.slice(0, -FRAME_TERMINATOR.length)
    : null;
}

export function gallagherTwxDataLoggerProcessMessage(
  device: DeviceLike,
  _type: string,
  _status: unknown,
  frame: string,
) {
  const deviceId = getDeviceId(device);
  const frameBody = stripFrameTerminator(frame);

  if (frameBody == null) {
    return [];
  }

  // Try the last known-good layout first.
  const fields = lastFieldToggles
    ? parseWithEid(frameBody, lastFieldToggles)
    : null;

  if (fields) {
    return buildScanActions(deviceId, fields);
  }

  // Cached layout didn't work (or there wasn't one) - autoconfigure from this frame
  // and retry once.
  const autoConfiguredFieldToggles = autoConfigure(frameBody);
  const autoConfiguredFields = parseWithEid(
    frameBody,
    autoConfiguredFieldToggles,
  );

  if (autoConfiguredFields) {
    lastFieldToggles = autoConfiguredFieldToggles;
    return buildScanActions(deviceId, autoConfiguredFields);
  }

  return [reportGallagherTwxDataLoggerConfigFailed(deviceId)];
}
